import { useState, useEffect, useContext, useMemo } from "react";
import { useMsal, } from '@azure/msal-react';
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import LoadingAnimation from "../loading/loading";
import { FloatingEmailInput } from "../reusable-elements/floating-email-input";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import { validEmail, notNullOrEmpty, validUKPhoneNumber, validateField, validUKPostCode } from "../../utils/validators";
import { TenantContext } from "../../pages/tenants/tenant-detail";
import { GoogleMap } from "../map/map";
import XeroApi from '../../api/xero/xero-api-factory';
import { ContactSelector } from "./contact-selector";
import { DateTime } from "luxon";
import _ from "lodash";
import { Link } from "react-router-dom";
import { googleMapsConfig } from "../../app";


import './tenants-style.css';

export const TenantDetailsTab = () => {

    //const comparer = new Comparer();

    const { instance, inProgress, accounts } = useMsal();
    const { tenant, setTenant, validationErrors, setValidationErrors } = useContext(TenantContext);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [rerender, setRerender] = useState(false);

    const [contacts, setContacts] = useState(null);
    const [contactsError, setContactsError] = useState(null);
    const [fetchingContacts, setFetchingContacts] = useState(false);

    const howLongUntilExpiry = expiry => {
        const now = DateTime.now();
        const expiryDateTime = DateTime.fromISO(expiry);
        const diff = expiryDateTime.diff(now, 'days');
        return Math.floor(diff.days);
    };

    const mapConfig = ({ lat, lng }) => {
        return {
            className: "h-100 w-100",
            style: { height: '100vh', width: '100%' },
            apiKey: googleMapsConfig.apiKey,
            center: {
                lat: lat,
                lng: lng
            },
            zoom: 17
        }
    };

    const xeroApi = useMemo(() => new XeroApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    useEffect(() => {
        async function fetchData() {
            if (fetchingContacts) return;

            setFetchingContacts(true);
            setContactsError(null); // Reset error state before fetching

            try {
                const result = _.sortBy(
                    await xeroApi.getCachedContacts(tenant.company.id),
                    c => c.contactName,
                    "desc"
                );
                setContacts(result);
            } catch (error) {
                console.error("An error occurred fetching Xero contacts:", error);

                // Check for authorization errors (typically 401 or 403)
                if (error.status === 401 || error.status === 403) {
                    setContactsError("You don't have permission to access Xero contacts");
                } else {
                    setContactsError("Failed to fetch Xero contacts");
                }

                // Set contacts to null or empty array depending on your UI needs
                setContacts(null);
            } finally {
                setFetchingContacts(false);
            }
        }

        if (tenant && tenant.company.xero && howLongUntilExpiry(tenant.company.xero.refreshTokenExpiresAtUtc) > 0) {
            fetchData();
        }
    }, [tenant, xeroApi]);

    useEffect(() => {
        if (!imageDimensions && !!tenant) {
            const img = new Image();
            img.onload = () => {
                const width = img.naturalWidth;
                const height = img.naturalHeight;

                setImageDimensions({ width, height });
            };
            img.src = tenant.thumbnailProfileImageIncludingSasToken;
        }

    }, [tenant, imageDimensions]);

    const validateState = newState => {
        // Validate fields that the user can change and that you care about the contents of
        validateField(() => newState.phoneNumber && notNullOrEmpty(newState.phoneNumber) && validUKPhoneNumber(newState.phoneNumber), () => newState.phoneNumber, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {
        const newState = updateMethod(tenant);
        validateState(newState);
        setTenant(newState);
        setRerender(!rerender);
    };

    const imageStyle = tenant => {
        const unknown = { alignSelf: "center", width: "50%", backgroundImage: 'url(/assets/img/user/user-6.jpg)' };
        const known = { borderRadius: "50%", backgroundRepeat: "no-repeat", width: `${imageDimensions ? imageDimensions.width : 128}px`, height: `${imageDimensions ? imageDimensions.height : 128}px`, backgroundImage: `url(${tenant.thumbnailProfileImageIncludingSasToken})` };
        return !!tenant && !!tenant.thumbnailProfileImageIncludingSasToken ? known : unknown;
    }

    // const canSave = () => {
    //     let result = hasChanges();
    //     result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
    //     return result;
    // };

    // const hasChanges = () => {
    //     const equal = comparer.equals(state, originalState);
    //     return !equal;
    // };

    return !!tenant ? (
        <>
            <div className="row mb-3">
                <div className="col-md-4" key={"Details"}>
                    <Panel
                        showReload={false}
                        showDelete={false}
                    >
                        <PanelHeader>Profile</PanelHeader>
                        <PanelBody>
                            <div className="row mb-15px">
                                <label className="form-label col-form-label col-md-3">Profile Image</label>
                                <div className={'col-md-9'}>
                                    {!!tenant && !!tenant.thumbnailProfileImageIncludingSasToken && <div className="img" style={imageStyle(tenant)}></div>}
                                    {!(!!tenant && !!tenant.thumbnailProfileImageIncludingSasToken) && <>Please select the profile image and crop it appropriately</>}
                                </div>
                            </div>

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.displayName ? "Enter" : ""} Display Name`}
                                text={tenant.displayName}
                                updateCallback={newValue => { handleStateUpdates(v => { v.displayName = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.displayName); }}

                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.firstName ? "Enter" : ""} First Name`}
                                text={tenant.firstName}
                                updateCallback={newValue => { handleStateUpdates(v => { v.firstName = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.firstName); }}

                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.lastName ? "Enter" : ""} Last Name`}
                                text={tenant.lastName}
                                updateCallback={newValue => { handleStateUpdates(v => { v.lastName = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.lastName); }}

                            />

                            <FloatingEmailInput
                                className={"mt-1"}
                                label={`${!tenant.emailAddress ? "Enter" : ""} Email Address`}
                                email={tenant.emailAddress}
                                updateCallback={newValue => { handleStateUpdates(v => { v.emailAddress = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.emailAddress) && validEmail(tenant.emailAddress); }}
                                readOnly={true}
                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={"Telegram Chat Id"}
                                text={tenant.telegramChatId}
                                updateCallback={newValue => { handleStateUpdates(v => { v.telegramChatId = newValue; return v; }) }}
                                readOnly={true}
                                validatorFn={() => { return notNullOrEmpty(tenant.telegramChatId); }}
                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.phoneNumber ? "Enter" : ""} Phone Number`}
                                text={tenant.phoneNumber}
                                updateCallback={newValue => { handleStateUpdates(v => { v.phoneNumber = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.phoneNumber) && validUKPhoneNumber(tenant.phoneNumber); }}
                                required={false}
                            />
                            {!!!contactsError &&
                                <div className="contact-selector ">
                                    <div className="mb-3px">Select Xero Contact</div>
                                    <ContactSelector
                                        contacts={contacts}
                                        onContactSelect={contact => { handleStateUpdates(v => { v.xeroContact = contact; return v; }) }}
                                        selectedContact={tenant.xeroContact}
                                        defaultContact={{ contactName: "None" }}
                                    />
                                </div>
                            }

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.locale ? "Enter" : ""} Locale`}
                                text={tenant.locale}
                                updateCallback={newValue => { handleStateUpdates(v => { v.locale = newValue; return v; }) }}
                                //validatorFn={() => { return notNullOrEmpty(tenant.locale); }}
                                validatorFn={() => { return true; }}
                                required={false}
                            />

                        </PanelBody>
                    </Panel>
                </div>

                <div className="col-md-4" key={"Address"}>
                    <Panel
                        showReload={false}
                        showDelete={false}
                    >
                        <PanelHeader>Address</PanelHeader>
                        <PanelBody>


                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.address.doorNumberOrName ? "Enter" : ""} Door Number or Name`}
                                text={tenant.address.doorNumberOrName}
                                updateCallback={newValue => { handleStateUpdates(v => { v.Address.doorNumberOrName = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.address.doorNumberOrName); }}

                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.address.roadName ? "Enter" : ""} Road Name`}
                                text={tenant.address.roadName}
                                updateCallback={newValue => { handleStateUpdates(v => { v.Address.roadName = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.address.roadName); }}

                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.address.town ? "Enter" : ""} Town Name`}
                                text={tenant.address.town}
                                updateCallback={newValue => { handleStateUpdates(v => { v.Address.town = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.address.town); }}

                            />

                            <FloatingTextInput
                                className={"mt-1"}
                                label={`${!tenant.address.postalCode ? "Enter" : ""} Post Code`}
                                text={tenant.address.postalCode}
                                updateCallback={newValue => { handleStateUpdates(v => { v.Address.postalCode = newValue; return v; }) }}
                                validatorFn={() => { return notNullOrEmpty(tenant.address.postalCode) && validUKPostCode(tenant.address.postalCode); }}

                            />

                            <Link to={`/property/detail/${tenant.propertyId}`} className="btn btn-primary mt-2" >Go to Property &nbsp;&nbsp;<i className="fas fa-house" /></Link>

                        </PanelBody>
                    </Panel>
                </div>

                <div className="col-md-4" key={"Map"}>
                    <Panel
                        showReload={false}
                        showDelete={false}
                    >
                        <PanelHeader>Map</PanelHeader>
                        <PanelBody>
                            <div>
                                {tenant.coordinates ? <GoogleMap {...mapConfig({ lat: tenant.coordinates.latitude, lng: tenant.coordinates.longitude })}
                                    markers={[{
                                        icon: "fa fa-location-dot",
                                        name: tenant.displayName,
                                        ...mapConfig({ lat: tenant.coordinates.latitude, lng: tenant.coordinates.longitude })
                                    }
                                    ]}>
                                </GoogleMap> : <>Not Yet Geocoded. Check the address.</>}
                            </div>
                        </PanelBody>
                    </Panel>
                </div>

            </div>

        </>
    )
        :
        <LoadingAnimation />
};